import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight } from "lucide-react";
import { marketplaceApi, type ProductItem } from "@/lib/api";

/** Fisher–Yates shuffle (returns a new array). */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ArrivalCard = ({ product }: { product: ProductItem }) => {
  const discount = product.discount_price
    ? Math.round(((+product.price - +product.discount_price) / +product.price) * 100)
    : 0;
  return (
    <Link
      to={`/marketplace/product/${product.slug}`}
      className="group/card relative block w-56 sm:w-60 shrink-0 bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-gold/40 transition-[box-shadow,border-color]"
    >
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        {product.main_image_url ? (
          <img
            src={product.main_image_url}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-300">
            <Package size={40} />
          </div>
        )}
        {discount > 0 && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold uppercase px-2 py-1 rounded">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-3.5">
        {product.category_name && (
          <p className="text-[10px] text-black tracking-wide mb-1 truncate">{product.category_name}</p>
        )}
        <h3 className="text-[13px] font-semibold text-black line-clamp-2 group-hover/card:text-gold transition-colors leading-snug min-h-[2.6em]">
          {product.name}
        </h3>
        <div className="mt-2">
          {product.discount_price ? (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-sm font-bold text-black">{product.currency || "$"}{product.discount_price}</span>
              <span className="text-[11px] text-gray-400 line-through">{product.currency || "$"}{product.price}</span>
            </div>
          ) : Number(product.price) > 0 ? (
            <span className="text-sm font-bold text-black">{product.currency || "$"}{product.price}</span>
          ) : (
            <span className="text-[11px] font-semibold text-gold">Request quote</span>
          )}
        </div>
      </div>
      {/* Animated bottom accent line on hover */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gold origin-left scale-x-0 group-hover/card:scale-x-100 transition-transform duration-300" />
    </Link>
  );
};

const NewArrivals = () => {
  const [products, setProducts] = useState<ProductItem[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await marketplaceApi.listProducts();
        if (alive) setProducts(shuffle(all).slice(0, 14));
      } catch {
        /* ignore — section stays empty */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (products.length === 0) return null;

  // Duplicate so the track loops seamlessly (-50%).
  const track = [...products, ...products];

  return (
    <section className="py-12 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black leading-snug">
              New arrivals
            </h2>
            <p className="text-sm text-black/70 mt-2 max-w-lg">
              Fresh products added by members across the AAMECC marketplace.
            </p>
          </div>
          <Link
            to="/marketplace/products"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline shrink-0"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 z-10 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 z-10 bg-gradient-to-l from-background to-transparent" />

        <div className="group flex overflow-hidden">
          <div className="flex shrink-0 gap-4 pr-4 animate-marquee-slow group-hover:[animation-play-state:paused]">
            {track.map((product, i) => (
              <ArrivalCard key={`${product.id}-${i}`} product={product} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewArrivals;
